import {
  Avatar,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment } from 'react'
import { Icons, SkeletonText } from 'utoniq-ui'

import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  favoriteMember: {
    id: string
    name: string
    avatar: string
    fans: {
      id: string
      displayName: string
      avatar: {
        uri: string
      }
      formattedCreatedAt: string
    }[]
  }
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ConsoleFansTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { routerPages } = useRouter()
  const { t } = useTranslation('consoleFans')

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleFavoriteMemberSettings,
          title: t('title'),
        }}
      >
        <Stack sx={{ pt: 2, pb: 5, px: 5 }}>
          {/* 推しメンバー情報 */}
          <FavoriteMemberHeaderCell
            favoriteMember={state.favoriteMember}
            loading={state.loading}
          />

          {/* 推し登録者一覧 */}
          <FavoriteFansListCell
            fans={state.favoriteMember.fans}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
          />
        </Stack>
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type FavoriteMemberHeaderCellProps = {
  favoriteMember: State['favoriteMember']
  loading: boolean
}

const FavoriteMemberHeaderCell: Cell<FavoriteMemberHeaderCellProps> = ({
  favoriteMember,
  loading,
}) => {
  if (loading) {
    return (
      <CellFrame sx={{ textAlign: 'center', py: 3 }}>
        <Skeleton
          variant="circular"
          width={80}
          height={80}
          sx={{ mx: 'auto', mb: 2 }}
        />
        <SkeletonText variant="h6" width="40%" sx={{ mx: 'auto' }} />
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ textAlign: 'center', py: 3 }}>
      <Avatar
        src={favoriteMember.avatar}
        sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
      >
        <Icons.Avatar />
      </Avatar>
      <Typography variant="h6">{favoriteMember.name}</Typography>
    </CellFrame>
  )
}

type FavoriteFansListCellProps = {
  fans: State['favoriteMember']['fans']
  loading: boolean
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
}

const FavoriteFansListCell: Cell<FavoriteFansListCellProps> = ({
  fans,
  loading,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleFans')

  return (
    <CellFrame>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ border: 'none', py: 2, px: 0, minWidth: 200 }}>
                <Typography variant="overline" color="text.hint">
                  {t('memberName')}
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  border: 'none',
                  py: 2,
                  px: 1,
                  minWidth: 120,
                  textAlign: 'right',
                }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('registrationDate')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            nodes={fans.map(fan => (
              <Fragment key={fan.id}>
                <TableCellCustom userId={fan.id} sx={{ px: 0, minWidth: 200 }}>
                  <Stack
                    direction="row"
                    sx={{ maxWidth: '100%' }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Avatar
                      src={fan.avatar?.uri}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
                      {fan.displayName}
                    </Typography>
                  </Stack>
                </TableCellCustom>
                <TableCellCustom
                  userId={fan.id}
                  sx={{ minWidth: 120, textAlign: 'right' }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {fan.formattedCreatedAt}
                  </Typography>
                </TableCellCustom>
              </Fragment>
            ))}
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            colSpan={2}
            component={TableRow}
            loadingComponent={
              <>
                {Array.from(Array(3).keys()).map(key => (
                  <TableRow key={key}>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <SkeletonText variant="medium" width="80%" />
                      </Stack>
                    </TableCell>
                    <TableCell padding="none" sx={{ px: 0, py: 3 }}>
                      <SkeletonText variant="caption" width={100} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            }
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

type TableCellCustomProps = {
  userId: string
  sx?: any
}
const TableCellCustom: Cell<TableCellCustomProps> = ({
  userId,
  children,
  sx,
}) => {
  const { router, generatePath, routerPages } = useRouter()

  const handleClick = () => {
    router.push(generatePath(routerPages.ConsoleUserDetail, { userId }))
  }

  return (
    <TableCell
      sx={{ px: 1, py: 3, cursor: 'pointer', ...sx }}
      onClick={handleClick}
    >
      {children}
    </TableCell>
  )
}
